import { useEffect, useRef, useState } from "react";
import { ActivityObject, ActivityReply } from "../../../@types/activities.ts";
import { find, isEmpty, map, reverse } from "lodash";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentDots, faSpinner } from "@fortawesome/pro-regular-svg-icons";
import ReplyComposer from "../replyComposer";
import { useQuery } from "react-query";
import Reply from "./replies/Reply.tsx";
import { useUpdateActivityReplyInCache } from "../../../hooks/useUpdateActivityReplyInCache.ts";
import { useParams, useSearchParams } from "../../../helpers/tixxt-router.tsx";

type RepliesArgs = {
  activityId: string;
  replies: ActivityObject["replies"];
  repliesCount: number;
  type?: "replies" | "answers";
  repliesClosed?: boolean;
  canReply?: boolean;
  showAllInitially?: boolean;
};

export default function Replies({
  replies,
  repliesCount,
  activityId,
  repliesClosed = false,
  canReply = true,
  type = "replies",
  showAllInitially = false,
}: RepliesArgs) {
  const { addReply } = useUpdateActivityReplyInCache();

  const [searchParams] = useSearchParams();
  const { commentId } = useParams();
  // Deep link to a reply, from a notification (?jump_to=..., #325) or the
  // /activities/:activityId/replies|answers/:commentId routes (API urls)
  const jumpTo = searchParams.get("jump_to") || commentId;

  const [showAll, setShowAll] = useState(showAllInitially || Boolean(jumpTo));
  const [replyToQuote, setReplyToQuote] = useState<ActivityReply | null>(null);
  const jumpedTo = useRef(false);

  const { data: allReplies, isLoading } = useQuery<ActivityReply[]>(
    `/activities/${activityId}/${type}`,
    {
      enabled: showAll,
    },
  );

  // Once the deep-linked reply is rendered (it may only arrive with the full
  // list), scroll it into the middle of the viewport — only once: later cache
  // updates (posting a reply, focus refetch) must not yank the viewport back.
  useEffect(() => {
    if (!jumpTo || jumpedTo.current) return;

    const element = document.getElementById(`reply-${jumpTo}`);
    if (!element) return;

    jumpedTo.current = true;
    requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [jumpTo, allReplies]);

  const repliesToShow = showAll && allReplies ? allReplies : replies;
  const sorting = Preload.current_network.config.activities?.reply_sorting;

  function quoteReply(reply: ActivityReply) {
    // Close all open dropdowns (stopPropagation in DropDownItem prevents body click)
    document
      .querySelectorAll(".tixxt__dropdown.show")
      .forEach((el) => el.classList.remove("show"));

    setReplyToQuote(reply);
  }

  // Scroll to the composer after it has opened with the quote
  useEffect(() => {
    if (replyToQuote) {
      requestAnimationFrame(() => {
        const element = document.getElementById("reply-composer");
        element?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  }, [replyToQuote]);

  const replyComponents = map(repliesToShow, (reply) => (
    <Reply
      key={reply.id}
      activityId={activityId}
      quoteReply={() => quoteReply(reply)}
      processType={type}
      {...reply}
    />
  ));

  const replyComposerContent = (
    <div id="reply-composer" key="replyComposer">
      {!canReply ? null : repliesClosed ? (
        type !== "answers" ? (
          <div
            className={classNames(
              "text-sm text-muted flex justify-center items-center pt-2",
              {
                "border-t-neutral border-t-1 border-dashed pt-4":
                  sorting === "oldest" && !isEmpty(replies),
                "border-b-neutral border-b-1 border-dashed pb-4":
                  sorting === "latest" && !isEmpty(replies),
              },
            )}
          >
            {I18n.t("js.activitystreams.comments.hint")}
          </div>
        ) : null
      ) : (
        <ReplyComposer
          type={type}
          className={classNames("bg-white", {
            "border-t-neutral border-t-1 border-dashed pt-4":
              sorting === "oldest" && !isEmpty(replies) && !window.isApp,
            "border-b-neutral border-b-1 border-dashed pb-4":
              sorting === "latest" && !isEmpty(replies) && !window.isApp,
          })}
          setQuoteData={setReplyToQuote}
          quoteData={replyToQuote}
          activityId={activityId}
          saveCallback={(data) =>
            addReply(activityId, type, {
              ...data,
              can: data.i.can,
              published: data.created_at,
              author: {
                ...data.author,
                displayName: data.author.display_name,
                images: {
                  ["40x40@2"]:
                    find(data.author.images, {
                      width: 80,
                      height: 80,
                    })?.url || "/assets/misc/profile_80x80.jpg",
                },
              },
              attachments: data.attachments,
              content: data.html_content,
              // the edit composer prefills from this — without it, editing a
              // freshly posted reply opens empty (#209)
              original_content: data.content,
            })
          }
        />
      )}
    </div>
  );

  if (!window.isApp || type !== "replies") {
    replyComponents.push(replyComposerContent);
  }

  return window.isApp && type === "replies" ? (
    <div className="flex flex-col h-full grow">
      {sorting === "latest" && (
        <div className="border-b-neutral border-b-1 px-5 pb-2.5 border-solid bg-white sticky top-9.5 z-10">
          {replyComposerContent}
        </div>
      )}
      <div className="flex-1 overflow-y-auto grow">
        <div className="flex flex-col gap-4 divide-y divide-neutral/25 my-5">
          {sorting === "latest" ? reverse(replyComponents) : replyComponents}
        </div>
      </div>
      {sorting === "oldest" && (
        <div className="border-t-neutral border-t-1 border-solid bg-white pt-2.5 sticky bottom-0 z-10 px-5 pb-5">
          {replyComposerContent}
        </div>
      )}
    </div>
  ) : (
    <div className="flex flex-col gap-4 pt-0! pb-4 print:block print:space-y-4">
      {/* Print always has the count above the comments, like the PDF */}
      {repliesCount > 0 && (
        <div
          className={classNames("flex justify-between items-center", {
            "hidden print:flex": repliesCount <= 3,
          })}
        >
          <span className="text-sm">
            {I18n.t(
              repliesCount === 1
                ? `js.global_search.activities.${type}.counting.one`
                : `js.global_search.activities.${type}.counting.other`,
              { count: repliesCount },
            )}
          </span>
          {repliesCount <= 3 ? null : isLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <button
              role="button"
              className="btn btn-sm flex items-center hover:underline gap-1 print:hidden"
              onClick={(e) => {
                e.preventDefault();
                setShowAll(!showAll);
              }}
            >
              <FontAwesomeIcon icon={faCommentDots} size="lg" />
              {I18n.t(
                `js.activitystreams.${showAll ? "collapse" : "expand"}_${type}`,
              )}
            </button>
          )}
        </div>
      )}
      <div
        className={classNames(
          "flex flex-col gap-4 print:block print:space-y-4",
          {
            "divide-y divide-neutral/25 -mx-5 px-5": window.isApp,
          },
        )}
      >
        {sorting === "latest" ? reverse(replyComponents) : replyComponents}
      </div>
    </div>
  );
}
